import { Button, Badge } from "@aix/ui"
import { useState } from "react"
import toast from "react-hot-toast"
import dayjs from "dayjs"
import type { Coach } from "../../../types/coach-os.types"
import { trpc } from "../../../lib/trpc"
import WeeklyPlanDetailsModal from "./WeeklyPlanDetailsModal"
import ManualPlanCreationModal from "./ManualPlanCreationModal"
import AIEnhancePlanModal from "./AIEnhancePlanModal"
import AdjustPlanModal from "./AdjustPlanModal"

interface WeeklyPlansTabProps {
  coachId: string
  coach: Coach
}

export default function WeeklyPlansTab({ coachId, coach }: WeeklyPlansTabProps) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEnhanceModal, setShowEnhanceModal] = useState(false)
  const [selectedPlanForDetails, setSelectedPlanForDetails] = useState<any | null>(null)
  const [selectedPlanForAdjustment, setSelectedPlanForAdjustment] = useState<string | null>(null)

  // Fetch weekly plans
  const {
    data: plansData,
    isLoading,
    refetch,
  } = trpc.plans.weeklyPlans.list.useQuery({
    coachId,
    limit: 50,
  })

  // Generate weekly plan mutation
  const generatePlanMutation = trpc.plans.weeklyPlans.generate.useMutation({
    onSuccess: () => {
      void refetch()
      toast.success("Weekly plan generated successfully!")
    },
    onError: (error) => {
      toast.error(error.message || "Failed to generate weekly plan")
    },
  })

  // Delete plan mutation
  const deletePlanMutation = trpc.plans.weeklyPlans.delete.useMutation({
    onSuccess: () => {
      void refetch()
      toast.success("Plan deleted successfully!")
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete plan")
    },
  })

  const weeklyPlans = plansData?.weeklyPlans || []

  const handleCreateManualPlan = () => {
    setShowCreateModal(true)
  }

  const handleAIEnhance = () => {
    setShowEnhanceModal(true)
  }

  const handleGeneratePlan = () => {
    if (coach.status !== "active") {
      toast.error("Coach must be active to generate plans")
      return
    }

    // Calculate current week number and dates
    const now = new Date()
    const startOfYear = new Date(now.getFullYear(), 0, 1)
    const weekNumber = Math.ceil(((now.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7)

    const weekStartDate = dayjs().startOf("week").format("YYYY-MM-DD")
    const weekEndDate = dayjs().endOf("week").format("YYYY-MM-DD")

    generatePlanMutation.mutate({
      coachId,
      weekNumber,
      weekStartDate,
      weekEndDate,
      phaseName: "Current Phase",
    })
  }

  const handleDeletePlan = (planId: string) => {
    if (confirm("Are you sure you want to delete this plan?")) {
      deletePlanMutation.mutate({ weeklyPlanId: planId })
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-primary text-primary-foreground"
      case "completed":
        return "bg-green-500 text-white"
      case "upcoming":
        return "bg-blue-500 text-white"
      case "skipped":
        return "bg-gray-500 text-white"
      default:
        return "bg-secondary text-secondary-foreground"
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Weekly Plans</h2>
          <p className="text-muted-foreground">Just-in-time weekly planning with expectations and daily structure</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleGeneratePlan}
            disabled={coach.status !== "active" || generatePlanMutation.isLoading}
            loading={generatePlanMutation.isLoading}
          >
            Generate This Week
          </Button>
          <Button onClick={handleAIEnhance} variant="outline">
            AI Enhance
          </Button>
          <Button onClick={handleCreateManualPlan} variant="outline">
            Create Manual Plan
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      {weeklyPlans.length > 0 && (
        <div className="bg-secondary rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Plans</p>
              <p className="text-lg font-semibold text-foreground">{weeklyPlans.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-lg font-semibold text-foreground">
                {weeklyPlans.filter((p) => p.status === "completed").length}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-lg font-semibold text-foreground">
                {weeklyPlans.filter((p) => p.status === "active").length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading plans...</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && weeklyPlans.length === 0 && (
        <div className="bg-secondary rounded-lg p-12 text-center border border-border">
          <div className="text-6xl mb-4">📅</div>
          <h3 className="text-xl font-semibold text-foreground mb-2">No Weekly Plans Yet</h3>
          <p className="text-muted-foreground mb-6">
            Generate your first weekly plan to get started with structured coaching
          </p>
          <Button
            onClick={handleGeneratePlan}
            disabled={coach.status !== "active" || generatePlanMutation.isLoading}
            loading={generatePlanMutation.isLoading}
          >
            Generate First Plan
          </Button>
        </div>
      )}

      {/* Plans Table */}
      {!isLoading && weeklyPlans.length > 0 && (
        <div className="bg-background rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b border-border">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Week #</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Week Range</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Phase</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Completion</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Generated</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {weeklyPlans.map((plan) => (
                  <tr key={plan.id} className="border-b border-border hover:bg-secondary/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-foreground">Week {plan.weekNumber}</td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {dayjs(plan.weekStartDate).format("MMM DD")} - {dayjs(plan.weekEndDate).format("MMM DD, YYYY")}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">{plan.phaseName || "-"}</td>
                    <td className="py-3 px-4">
                      <Badge className={getStatusColor(plan.status)}>{plan.status}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {plan.completionCount} / {plan.totalActions}
                      <span className="ml-2 text-xs">
                        ({plan.totalActions > 0 ? Math.round((plan.completionCount / plan.totalActions) * 100) : 0}%)
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {dayjs(plan.generatedAt).format("MMM DD, YYYY")}
                      {plan.adjusted && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Adjusted
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setSelectedPlanForDetails(plan)}>
                          View
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setSelectedPlanForAdjustment(plan.id)}>
                          Adjust
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletePlan(plan.id)}
                          disabled={deletePlanMutation.isLoading}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expectations Preview */}
      {!isLoading && weeklyPlans.length > 0 && (
        <div className="bg-secondary rounded-lg p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Latest Plan Details</h3>
          {weeklyPlans[0] && (
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-foreground mb-2">Expectations:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  {((weeklyPlans[0].expectations as string[]) || []).map((exp, idx) => (
                    <li key={idx}>{exp}</li>
                  ))}
                </ul>
              </div>
              {weeklyPlans[0].dailyStructure && (
                <div>
                  <h4 className="font-medium text-foreground mb-2">Daily Structure:</h4>
                  <pre className="text-xs text-muted-foreground bg-background p-3 rounded border border-border overflow-x-auto">
                    {JSON.stringify(weeklyPlans[0].dailyStructure, null, 2)}
                  </pre>
                </div>
              )}
              {weeklyPlans[0].adjustmentReason && (
                <div>
                  <h4 className="font-medium text-foreground mb-2">Adjustment Reason:</h4>
                  <p className="text-sm text-muted-foreground">{weeklyPlans[0].adjustmentReason}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-secondary rounded-lg p-4 border border-border">
          <div className="text-3xl mb-2">🤖</div>
          <h4 className="font-semibold text-foreground mb-1">AI Full Mode</h4>
          <p className="text-sm text-muted-foreground">
            Let AI generate complete weekly plans based on your quarterly goals
          </p>
        </div>
        <div className="bg-secondary rounded-lg p-4 border border-border">
          <div className="text-3xl mb-2">✨</div>
          <h4 className="font-semibold text-foreground mb-1">AI Enhance Mode</h4>
          <p className="text-sm text-muted-foreground">Brain dump your week and AI structures it for you</p>
        </div>
        <div className="bg-secondary rounded-lg p-4 border border-border">
          <div className="text-3xl mb-2">✍️</div>
          <h4 className="font-semibold text-foreground mb-1">Manual Mode</h4>
          <p className="text-sm text-muted-foreground">Create and structure your own weekly plans</p>
        </div>
      </div>

      {/* View Plan Details Modal */}
      <WeeklyPlanDetailsModal
        plan={selectedPlanForDetails}
        isOpen={!!selectedPlanForDetails}
        onClose={() => setSelectedPlanForDetails(null)}
      />

      {/* Manual Plan Creation Modal */}
      <ManualPlanCreationModal
        coachId={coachId}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={refetch}
      />

      {/* AI Enhance Modal */}
      <AIEnhancePlanModal
        coachId={coachId}
        coachName={coach.name}
        isOpen={showEnhanceModal}
        onClose={() => setShowEnhanceModal(false)}
        onSuccess={refetch}
      />

      {/* Adjust Plan Modal */}
      <AdjustPlanModal
        weeklyPlanId={selectedPlanForAdjustment}
        coachName={coach.name}
        isOpen={!!selectedPlanForAdjustment}
        onClose={() => setSelectedPlanForAdjustment(null)}
        onSuccess={refetch}
      />
    </div>
  )
}
